<script setup lang="ts">
import type { DateRangePickerFieldValue } from '@/components/pickerFields/DateRangePickerField.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { reactive, ref, toRaw } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import KSingleAutocompleteField from '@/components/fields/KSingleAutocompleteField.vue';
import PostCodeAutocompleteField from '@/components/fields/PostCodeAutocompleteField.vue';
import DateRangePickerField from '@/components/pickerFields/DateRangePickerField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  postCodeItem?: openapi.SuburbOpenapi | null;
  distance?: number | null;
  dates?: DateRangePickerFieldValue | null;
}

export interface ValidData {
  postCodeItem: openapi.SuburbOpenapi;
  distance: number;
  dates: DateRangePickerFieldValue;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  const _formData = {
    ...toRaw(formData),
    postCodeItem: toRaw(formData.postCodeItem),
  };
  emit('submit', _formData as ValidData);
});

const distanceItems = [
  {
    value: 20,
    title: '20 km',
  },
  {
    value: 50,
    title: '50 km',
  },
  {
    value: 100,
    title: '100 km',
  },
  {
    value: 200,
    title: '200 km',
  },
];
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.postCodeSearchSearchForm"
    data-testid="postCodeSearchSearchForm"
    @submit.prevent="handleSubmit"
  >
    <PostCodeAutocompleteField
      v-model="formData.postCodeItem"
      size="lg"
      label="Postcode"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="[$style.field, $style.postCodeField]"
    />
    <KSingleAutocompleteField
      v-model="formData.distance"
      size="lg"
      label="Distance"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="[$style.field, $style.distanceField]"
      :items="distanceItems"
    />
    <DateRangePickerField
      v-model="formData.dates"
      size="lg"
      label="Date range"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.field"
    />
    <KBtn
      type="submit"
      size="lg"
      prepend-icon="mdi-magnify"
      :loading="props.loading"
      :class="$style.submit"
    >
      Search
    </KBtn>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.postCodeSearchSearchForm {
}
  .field {
    width: 17.5rem;
    display: inline-block;
    vertical-align: top;

    &.postCodeField {
      width: 11rem;
    }
    &.distanceField {
      width: 11rem;
    }
  }
  .field {
    margin-right: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .submit {
    margin-bottom: calc(0.5rem + 22px);
  }
</style>
